/**
 * Beschreibungs-Texte (v2): je Bereich der Lagedaten-Tabelle eine Textbox
 * in der Mini-Template-Sprache, plus je Ebene eine Fazit-Box.
 * Die Gliederung stammt aus dem aktiven Report-Layout und ist hier nicht editierbar.
 */
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  getDescriptionConfig,
  saveDescriptionConfig,
  getDescriptionPreview,
  getDescriptionStructure,
  getDescriptionVariables,
  getDescriptionWarnings,
} from '../services/gutachtenApi'
import type {
  DescriptionStructure,
  DescriptionVariable,
  DescriptionVariables,
  DescriptionWarnings,
} from '../services/gutachtenApi'

type Ebene = 'makro' | 'mikro'

interface DescriptionConfigV2 {
  version?: number
  na?: string
  headings?: Record<string, string>
  fazit_titel?: Record<string, string>
  templates: Record<string, Record<string, string>>
  fallbacks: Record<string, Record<string, string>>
  words?: Record<string, any>
  thresholds?: Record<string, any>
  join?: Record<string, any>
}

const FAZIT = 'fazit'
/** Überschrift der Variablen ohne eigenen Tabellenbereich (bereich_key === null). */
const OHNE_BEREICH = 'Wertungen & Kontext'
const EBENEN: Ebene[] = ['makro', 'mikro']
const EBENEN_LABEL: Record<Ebene, string> = { makro: 'Makrolage', mikro: 'Mikrolage' }

const INPUT =
  'px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary'

export function DescriptionConfigEditor() {
  const [cfg, setCfg] = useState<DescriptionConfigV2 | null>(null)
  const [structure, setStructure] = useState<DescriptionStructure | null>(null)
  const [variables, setVariables] = useState<DescriptionVariables | null>(null)
  const [warnings, setWarnings] = useState<DescriptionWarnings>({})
  const [ags, setAgs] = useState('08315064')
  const [preview, setPreview] = useState<any>(null)
  const [previewError, setPreviewError] = useState('')
  const [status, setStatus] = useState('')
  const [focus, setFocus] = useState<{ ebene: Ebene; key: string } | null>(null)
  const [showHelp, setShowHelp] = useState(false)
  const [sharedText, setSharedText] = useState('')
  const [sharedError, setSharedError] = useState('')

  const areaRefs = useRef<Record<string, HTMLTextAreaElement | null>>({})
  const pendingCaret = useRef<{ id: string; pos: number } | null>(null)

  useEffect(() => {
    getDescriptionConfig()
      .then((r) => {
        setCfg(r.config)
        setSharedText(
          JSON.stringify(
            { words: r.config?.words ?? {}, thresholds: r.config?.thresholds ?? {}, join: r.config?.join ?? {} },
            null,
            2,
          ),
        )
      })
      .catch((e: any) => setStatus('Fehler beim Laden: ' + (e?.response?.data?.detail ?? e.message)))
    getDescriptionStructure()
      .then(setStructure)
      .catch((e: any) => setStatus('Fehler beim Laden der Gliederung: ' + (e?.response?.data?.detail ?? e.message)))
  }, [])

  /** Vorschau, Variablen und Warnungen hängen alle am AGS. */
  const reloadForAgs = async (agsToUse: string) => {
    setPreviewError('')
    try {
      const [pv, vars, warn] = await Promise.all([
        getDescriptionPreview(agsToUse),
        getDescriptionVariables(agsToUse),
        getDescriptionWarnings(agsToUse),
      ])
      setPreview(pv)
      setVariables(vars)
      setWarnings(warn)
    } catch (e: any) {
      setPreview(null)
      setPreviewError(
        e?.response?.status === 404
          ? `Keine Gemeinde mit AGS ${agsToUse} gefunden.`
          : 'Fehler: ' + (e?.response?.data?.detail ?? e.message),
      )
    }
  }

  useEffect(() => {
    if (cfg) reloadForAgs(ags)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg !== null])

  // Nach dem Einfügen einer Variable den Cursor hinter das Eingefügte setzen
  // (erst nachdem React den neuen Wert ins Textfeld geschrieben hat).
  useEffect(() => {
    const pending = pendingCaret.current
    if (!pending) return
    pendingCaret.current = null
    const node = areaRefs.current[pending.id]
    if (node) {
      node.focus()
      node.setSelectionRange(pending.pos, pending.pos)
    }
  })

  if (!cfg || !structure) return <div className="p-6">Lädt…</div>

  const update = (fn: (c: DescriptionConfigV2) => void) => {
    const clone = structuredClone(cfg)
    fn(clone)
    setCfg(clone)
  }

  const onSave = async () => {
    if (sharedError) {
      setStatus('Nicht gespeichert: Bausteine-JSON ist ungültig.')
      return
    }
    try {
      await saveDescriptionConfig({ config: cfg })
      setStatus('Gespeichert.')
      await reloadForAgs(ags)
    } catch (e: any) {
      setStatus('Fehler: ' + (e?.response?.data?.detail ?? e.message))
    }
  }

  /** Fügt Text an der Cursorposition der fokussierten Box ein. */
  const insert = (snippet: string) => {
    if (!focus) return
    const id = `${focus.ebene}.${focus.key}`
    const el = areaRefs.current[id]
    const current = cfg.templates?.[focus.ebene]?.[focus.key] ?? ''
    const start = el ? el.selectionStart : current.length
    const end = el ? el.selectionEnd : current.length
    const next = current.slice(0, start) + snippet + current.slice(end)
    update((c) => {
      c.templates = c.templates ?? {}
      c.templates[focus.ebene] = c.templates[focus.ebene] ?? {}
      c.templates[focus.ebene][focus.key] = next
    })
    pendingCaret.current = { id, pos: start + snippet.length }
  }

  const boxesFor = (ebene: Ebene): { key: string; titel: string; rating_key?: string | null }[] => [
    ...structure[ebene],
    // Der Titel gehört wie jeder andere Wortlaut in die Config; fehlt er, steht hier
    // ein neutraler Platzhalter statt eines deutschen Wortes aus dem Code.
    { key: FAZIT, titel: cfg.fazit_titel?.[ebene] || '—', rating_key: null },
  ]

  const renderBox = (ebene: Ebene, box: { key: string; titel: string; rating_key?: string | null }) => {
    const id = `${ebene}.${box.key}`
    const isFocused = focus?.ebene === ebene && focus?.key === box.key
    const unknown = warnings[id] ?? []
    return (
      <div
        key={id}
        className={`bg-gut8-bg-muted border rounded-gut8 p-3 space-y-2 ${
          isFocused ? 'border-primary' : 'border-border-gut8-light'
        }`}
      >
        <div className="text-sm font-semibold text-gut8-anthracite">
          {box.titel}{' '}
          <span className="text-text-muted font-normal font-mono text-xs">
            {box.key}
            {box.rating_key ? ` · Rating: ${box.rating_key}` : ''}
          </span>
        </div>
        <textarea
          ref={(el) => {
            areaRefs.current[id] = el
          }}
          className="w-full h-36 text-sm font-mono p-2 leading-relaxed border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          value={cfg.templates?.[ebene]?.[box.key] ?? ''}
          placeholder="Eine Zeile = ein Satz. Leerzeile = Absatz."
          onFocus={() => setFocus({ ebene, key: box.key })}
          onChange={(e) =>
            update((c) => {
              c.templates = c.templates ?? {}
              c.templates[ebene] = c.templates[ebene] ?? {}
              c.templates[ebene][box.key] = e.target.value
            })
          }
        />
        <div className="space-y-1">
          <div className="text-xs font-medium text-text-muted">
            Ersatzsatz (greift, wenn mangels Daten keine Zeile übrig bleibt)
          </div>
          <input
            className={`w-full ${INPUT}`}
            value={cfg.fallbacks?.[ebene]?.[box.key] ?? ''}
            onChange={(e) =>
              update((c) => {
                c.fallbacks = c.fallbacks ?? {}
                c.fallbacks[ebene] = c.fallbacks[ebene] ?? {}
                c.fallbacks[ebene][box.key] = e.target.value
              })
            }
          />
        </div>
        {unknown.length > 0 && (
          <div className="text-xs text-secondary bg-secondary-subtle/40 border border-secondary/40 rounded-gut8 px-2 py-1.5">
            Unbekannte Variablen: <span className="font-mono">{unknown.join(', ')}</span> — sie erscheinen unverändert
            (sichtbar) im Text. Speichern ist trotzdem möglich.
          </div>
        )}
      </div>
    )
  }

  /** Variablen der Ebene, nach Bereich gruppiert; Bereich der fokussierten Box zuerst. */
  const groupedVariables = (ebene: Ebene): { key: string; titel: string; vars: DescriptionVariable[] }[] => {
    const list = variables?.[ebene] ?? []
    const groups: { key: string; titel: string; vars: DescriptionVariable[] }[] = []
    for (const v of list) {
      let g = groups.find((x) => x.key === v.bereich_key)
      if (!g) {
        // Variablen ohne Bereich (Wertungen, Kontextfelder, abgeleitete Fazit-Variablen)
        // hätten sonst eine leere Überschrift.
        g = { key: v.bereich_key, titel: v.bereich_titel || v.bereich_key || OHNE_BEREICH, vars: [] }
        groups.push(g)
      }
      g.vars.push(v)
    }
    if (focus?.ebene === ebene) {
      const i = groups.findIndex((g) => g.key === focus.key)
      if (i > 0) groups.unshift(groups.splice(i, 1)[0])
    }
    return groups
  }

  const renderPicker = () => {
    if (!focus) {
      return <div className="text-sm text-text-muted">Eine Textbox anklicken, um deren Variablen zu sehen.</div>
    }
    if (!variables) {
      return <div className="text-sm text-text-muted">Variablen konnten für diesen AGS nicht geladen werden.</div>
    }
    const groups = groupedVariables(focus.ebene)
    if (groups.length === 0) return <div className="text-sm text-text-muted">Keine Variablen vorhanden.</div>
    return (
      <div className="space-y-3">
        {groups.map((g, gi) => (
          <div key={g.key}>
            <div className="text-xs font-semibold text-gut8-anthracite uppercase tracking-wide mb-1">
              {g.titel}
              {gi === 0 && focus.key === g.key && <span className="ml-1 text-primary normal-case">· aktuelle Box</span>}
            </div>
            <div className="space-y-1.5">
              {g.vars.map((v) => (
                <div key={v.name} className="border border-border-gut8-light rounded-gut8 bg-white px-2 py-1.5">
                  {v.nur_gruppierung ? (
                    <div className="text-sm text-text-muted">
                      {v.label} <span className="text-xs">(nur Untervarianten einsetzbar)</span>
                    </div>
                  ) : (
                    <button
                      className="text-sm text-left font-medium text-primary hover:text-primary-hover"
                      onClick={() => insert(`{${v.name}}`)}
                    >
                      {v.label}
                    </button>
                  )}
                  <div className="text-[11px] text-text-muted font-mono break-all">
                    {v.name}
                    {v.beispiel ? <span className="font-sans"> · z. B. {v.beispiel}</span> : null}
                  </div>
                  {v.untervarianten.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {v.untervarianten.map((sub) => (
                        <button
                          key={sub}
                          className="text-[11px] font-mono bg-primary-subtle text-primary px-1.5 py-0.5 rounded hover:bg-primary hover:text-text-inverse"
                          onClick={() => insert(`{${v.name}.${sub}}`)}
                        >
                          .{sub}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  const onSharedChange = (text: string) => {
    setSharedText(text)
    try {
      const parsed = JSON.parse(text)
      setSharedError('')
      update((c) => {
        c.words = parsed.words ?? {}
        c.thresholds = parsed.thresholds ?? {}
        c.join = parsed.join ?? {}
      })
    } catch (e: any) {
      // Ungültiges JSON: Zustand der Config bleibt unverändert erhalten
      setSharedError(e.message)
    }
  }

  const previewSections: any[] = [
    ...(preview?.makro_sections ?? []).map((s: any) => ({ ...s, _k: `makro.${s.key}` })),
    ...(preview?.mikro_sections ?? []).map((s: any) => ({ ...s, _k: `mikro.${s.key}` })),
  ]

  return (
    <div className="p-6 space-y-4">
      <div className="flex gap-2 items-center">
        <h1 className="text-2xl font-bold text-gut8-anthracite flex-1">Beschreibungs-Texte</h1>
        <input className={`w-32 ${INPUT}`} value={ags} onChange={(e) => setAgs(e.target.value)} placeholder="AGS" />
        <button
          className="inline-flex items-center gap-2 px-4 h-9 rounded-gut8 border border-border-gut8-medium bg-white text-gut8-anthracite text-sm hover:bg-gut8-bg-muted"
          onClick={() => reloadForAgs(ags)}
        >
          Vorschau
        </button>
        <button
          className="inline-flex items-center gap-2 px-4 h-9 rounded-gut8 bg-primary text-text-inverse font-semibold text-sm hover:bg-primary-hover disabled:opacity-40"
          onClick={onSave}
        >
          Speichern
        </button>
      </div>
      {status && <div className="text-sm text-text-muted">{status}</div>}

      <div className="text-sm text-text-muted bg-gut8-bg-muted border border-border-gut8-light rounded-gut8 px-3 py-2">
        Die Gliederung (welche Boxen es gibt und in welcher Reihenfolge) stammt aus der Lagedaten-Tabelle und ist hier
        nicht editierbar. Bereiche anlegen, umbenennen oder umsortieren im{' '}
        <Link className="font-medium text-primary hover:text-primary-hover" to="/admin/report-layout">
          Layout-Builder
        </Link>
        .
      </div>

      <div className="bg-white border border-border-gut8-light rounded-gut8 shadow-gut8-sm">
        <button
          className="w-full text-left px-4 py-2 text-sm font-semibold text-gut8-anthracite"
          onClick={() => setShowHelp(!showHelp)}
        >
          {showHelp ? '▾' : '▸'} Schreibweise (Kurzhilfe)
        </button>
        {showHelp && (
          <div className="px-4 pb-3 text-sm text-gut8-anthracite space-y-1">
            <div>
              <b>Jede Zeile ist ein Satz.</b> Fehlt ein darin verwendeter Wert, entfällt die ganze Zeile. Eine
              Leerzeile erzeugt einen Absatz.
            </div>
            <div>
              <span className="font-mono">{'{einwohner}'}</span> setzt einen Wert ein;{' '}
              <span className="font-mono">{'{einwohner.bund}'}</span>,{' '}
              <span className="font-mono">{'{einwohner.jahr}'}</span>,{' '}
              <span className="font-mono">{'{einwohner.richtung}'}</span>,{' '}
              <span className="font-mono">{'{einwohner.trend}'}</span>,{' '}
              <span className="font-mono">{'{einwohner.abs}'}</span> sind Untervarianten.
            </div>
            <div>
              <span className="font-mono">{'{rating.demographische_lage}'}</span> setzt eine Wertung als Wort ein.
            </div>
            <div>
              <span className="font-mono">{'{quote:1}'}</span> erzwingt eine Nachkommastelle (ohne Angabe gelten die
              Nachkommastellen der Tabellenzeile).
            </div>
            <div>
              <span className="font-mono">{'[[ … ]]'}</span> ist ein optionaler Teil: er entfällt, wenn ein Wert darin
              fehlt. <span className="font-mono">{'[[!name … ]]'}</span> erscheint nur, wenn{' '}
              <span className="font-mono">name</span> leer ist.
            </div>
            <div>
              <span className="font-mono">{'{list: A | B | C}'}</span> verbindet die nicht-leeren Teile zu einer
              Aufzählung.
            </div>
            <div className="text-text-muted">
              Ein falsch geschriebener Name bleibt absichtlich sichtbar im Text stehen — so fallen Tippfehler auf.
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_0.8fr_1fr] gap-4">
        <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-2">
          {EBENEN.map((ebene) => (
            <div key={ebene}>
              <div className="text-sm font-semibold text-gut8-anthracite mb-2">{EBENEN_LABEL[ebene]}</div>
              <div className="space-y-3">{boxesFor(ebene).map((b) => renderBox(ebene, b))}</div>
            </div>
          ))}

          <div className="bg-white border border-border-gut8-light rounded-gut8 shadow-gut8-sm p-4 space-y-2">
            <div className="text-sm font-semibold text-gut8-anthracite">
              Geteilte Bausteine (Wörter, Schwellen, Aufzählung)
            </div>
            <textarea
              className="w-full h-64 text-xs font-mono p-2 leading-relaxed border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              value={sharedText}
              onChange={(e) => onSharedChange(e.target.value)}
            />
            {sharedError ? (
              <div className="text-xs text-secondary">Ungültiges JSON — Änderung wird nicht übernommen: {sharedError}</div>
            ) : (
              <div className="text-xs text-text-muted">JSON gültig.</div>
            )}
          </div>
        </div>

        <div className="bg-white border border-border-gut8-light rounded-gut8 shadow-gut8-sm p-4 space-y-2 max-h-[75vh] overflow-y-auto">
          <div className="text-sm font-semibold text-gut8-anthracite">Variablen</div>
          {renderPicker()}
        </div>

        <div className="bg-white border border-border-gut8-light rounded-gut8 shadow-gut8-sm p-4 space-y-3 max-h-[75vh] overflow-y-auto">
          <div className="text-sm font-semibold text-gut8-anthracite">Vorschau (gespeicherter Stand)</div>
          <div className="text-xs text-text-muted">
            Vorschau und Warnungen rendert der Server aus der gespeicherten Config — Änderungen in den Boxen
            erscheinen erst nach „Speichern".
          </div>
          {previewError && <div className="text-sm text-secondary">{previewError}</div>}
          {preview ? (
            <div className="space-y-3">
              {previewSections.map((s) => (
                <div key={s._k} className="border border-border-gut8-light rounded-gut8 p-3">
                  <div className="text-sm font-semibold text-gut8-anthracite">
                    {s.title}
                    {s.rating && (
                      <span className="text-xs text-primary font-medium ml-2">
                        {s.rating.term} ({s.rating.score})
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gut8-anthracite mt-1 whitespace-pre-line">
                    {s.text || <span className="text-text-muted italic">(leer)</span>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            !previewError && <div className="text-sm text-text-muted">Lädt Vorschau…</div>
          )}
        </div>
      </div>
    </div>
  )
}
